import { getRequest, postRequest } from '@lib/util/http';

import {
  PostLoginRequest,
  LoginFormValues
} from '~/modules/login/login-form/constants';
import {
  PostRegisterRequest,
  RegisterFormValues
} from '~/modules/register/register-form/constants';
import {
  PostVerifyRequest,
  VerifyFormValues
} from '~/modules/verify/verify-form/constants';

import { DataResponseBase, APIError, APIResponse } from './common';
import { authController } from './consts';

export const LOGIN_ERROR_CODE = 333;

export enum ROLE {
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
}

// Google Auth types based on backend schemas
export type GoogleAuthResponse = {
  email: string
  userName: string
  accessToken: string
  refreshToken: string
}

export type PostLoginGoogleResponseSuccess =
  DataResponseBase<GoogleAuthResponse>
export type PostLoginGoogleResponse = PostLoginGoogleResponseSuccess | APIError

// User Info types
export type BackofficeUserInfoResponse = {
  email: string
  userName: string
}

export type GetUserInfoResponseSuccess =
  DataResponseBase<BackofficeUserInfoResponse>
export type GetUserInfoResponse = GetUserInfoResponseSuccess | APIError

// Legacy login types (mantener por compatibilidad)
export type PostLoginResponseSuccess = {
  data: {
    email: string
    userName: string
    accessToken: string
    refreshToken: string
    role: string
  }
}

export type PostLoginResponse = PostLoginResponseSuccess | APIError

export type PostLoginPayload = LoginFormValues

export type PostLoginQuery = (
  newData: PostLoginPayload
) => Promise<PostLoginResponse>

export const postLogin = async (UserPayload: PostLoginRequest) =>
  postRequest<PostLoginResponse, PostLoginRequest>(
    `${authController}/login`,
    UserPayload,
    {
      headers: {
        Authorization: `Basic ${btoa(`${UserPayload.email}:${UserPayload.password}`)}`
      }
    }
  );

export const ERROR_CODES = {
  LOGIN: 333,
  USER_EXISTS: 334,
  FILE_SIZE: 335,
  ONLY_IMAGES: 336
} as const;

export type PostRegisterResponseError = {
  err: 'P2002'
  message: string
  code: (typeof ERROR_CODES)[keyof typeof ERROR_CODES]
}

export type PostRegisterResponseSuccess = {
  data: {
    email: string
  }
}

export type PostRegisterResponse =
  | PostRegisterResponseSuccess
  | PostRegisterResponseError

export type PostRegisterPayload = RegisterFormValues

export type PostRegisterQuery = (
  newData: PostRegisterPayload
) => Promise<PostRegisterResponse>

export const postRegister = async (UserPayload: PostRegisterRequest) =>
  postRequest<PostRegisterResponse, PostRegisterRequest>(
    `${authController}/register`,
    UserPayload
  );

export type PostVerifyResponseSuccess = {
  data: {
    email: string
    userName: string
    accessToken: string
    refreshToken: string
    role: string
  }
}

export type PostVerifyResponse = PostVerifyResponseSuccess | APIError

export type PostVerifyPayload = VerifyFormValues

export type PostVerifyQuery = (
  newData: PostVerifyPayload
) => Promise<PostVerifyResponse>

export const postVerify = async (UserPayload: PostVerifyRequest) =>
  postRequest<PostVerifyResponse, PostVerifyRequest>(
    `${authController}/validate`,
    UserPayload
  );

export type PostResendResponseError = {
  err: 'P2002'
  message: string
}

export type PostResendResponseSuccess = {
  ok: boolean
}

export type PostResendResponse =
  | PostResendResponseSuccess
  | PostResendResponseError

export type PostResendPayload = { email: string }

export type PostResendQuery = (
  newData: PostResendPayload
) => Promise<PostResendResponse>

export const postResend = async (UserPayload: PostResendPayload) =>
  postRequest<PostResendResponse, PostResendPayload>(
    `${authController}/resend-code`,
    UserPayload
  );

export type PostForgotRequest = {
  email: string
}

export type PostForgotResponseSuccess = {
  ok: boolean
}

export type PostForgotResponse = PostForgotResponseSuccess | APIError

export type PostForgotPayload = {
  email: string
}

export type PostForgotQuery = (
  newData: PostForgotPayload
) => Promise<PostForgotResponse>

export const postForgot = async (payload: PostForgotRequest) =>
  postRequest<PostForgotResponse, PostForgotRequest>(
    `${authController}/forgot-password`,
    payload
  );

export type PostResetPasswordRequest = {
  code: string
  password: string
}

export type PostResetPasswordResponseSuccess = {
  ok: boolean
}

export type PostResetPasswordResponse =
  | PostResetPasswordResponseSuccess
  | APIError

export type PostResetPasswordQuery = (
  newData: PostResetPasswordRequest
) => Promise<PostResetPasswordResponse>

export const postResetPassword = async (payload: PostResetPasswordRequest) =>
  postRequest<PostResetPasswordResponse, PostResetPasswordRequest>(
    `${authController}/reset-password`,
    payload
  );

// Refresh Token types - usa la misma respuesta que Google Auth
export type GetRefreshTokenResponseSuccess =
  DataResponseBase<GoogleAuthResponse>
export type GetRefreshTokenResponse = GetRefreshTokenResponseSuccess | APIError
export type GetRefreshTokenQuery = () => Promise<GetRefreshTokenResponse>

// Backoffice API endpoints
export const refreshRoute = `${authController}/refresh/`;
export const userInfoRoute = `${authController}/user_info/`;
export const googleAuthRoute = `${authController}/google/`;

// Google Auth Types and Functions
export type PostLoginGoogleRequest = {
  token: string
}

// Tipo para la respuesta real del backend (mantener para compatibilidad con el hook)
export type PostLoginGoogleBackendResponse = {
  status_code: number
  content: {
    error: boolean
    detail: null | string
    data: {
      email: string
      userName: string
      accessToken: string
      refreshToken: string
    }
  }
}

export type PostLoginGoogleQuery = (
  newData: PostLoginGoogleRequest
) => Promise<PostLoginGoogleResponse>

export const postLoginGoogle = async (payload: PostLoginGoogleRequest) =>
  postRequest<APIResponse<GoogleAuthResponse>, PostLoginGoogleRequest>(
    googleAuthRoute,
    payload
  );

export const getUserInfo = async () =>
  getRequest<APIResponse<BackofficeUserInfoResponse>>(userInfoRoute);

export const getRefreshToken = async () =>
  getRequest<APIResponse<GoogleAuthResponse>>(refreshRoute);
